import { useIsFocused } from '@react-navigation/native'
import { infiniteQueryOptions, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { getIsLoggedIn } from '../../auth/utils/getIsLoggedIn'
import { getStatusOptions } from '../../notification/services/useStatusQuery'
import type { TMediumUserApi } from '../../user/api/userApi.types'
import { messageApi } from '../api/messageApi'
import type { TConversationApi, TFullMessageApi } from '../api/types/messageApi.types'
import { useConversationsListStore } from '../stores/useConversationsListStore'

export const getConversationsPreviewOptions = ({
  disabled,
  unread = false,
  customLimit,
}: { disabled?: boolean; unread?: boolean; customLimit?: number } = {}) =>
  infiniteQueryOptions({
    queryKey: ['conversations', unread],
    queryFn: ({ signal, pageParam: queryParam }) => messageApi.getConversations({ ...queryParam, unread }, { signal }),
    getNextPageParam: (lastPage, _pages, { limit, offset }) =>
      lastPage.length === limit ? { offset: offset + limit, limit } : null,
    initialPageParam: { offset: 0, limit: customLimit || 12 },
    retry: 0,
    // We want to refetch all pages ( to avoid inconsistent data )
    refetchInterval: 15000,
    staleTime: 15 * 1000, // 10 seconds
    select: ({ pages }) => pages.flat(),
    enabled: getIsLoggedIn() && !disabled,
  })

export const useConversationsPreviewQuery = (customLimit?: number) => {
  const queryClient = useQueryClient()
  const isFocused = useIsFocused()
  const unread = useConversationsListStore((state) => state.unread)

  const query = useInfiniteQuery(getConversationsPreviewOptions({ disabled: !isFocused, unread, customLimit }))

  /** Update the status to update the unread conversations count */
  useEffect(() => {
    if (query.data) {
      queryClient.invalidateQueries({ queryKey: getStatusOptions().queryKey })
    }
  }, [queryClient, query.data])

  const addInConversationPreview = (message: TFullMessageApi, contact: TMediumUserApi) => {
    const conversation: TConversationApi = {
      id: message.conversationId,
      createdAt: message.createdAt,
      updatedAt: message.updatedAt,
      conversationUsers: [
        {
          id: contact.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          user: contact,
        },
      ],
      hasUnreadMessages: false,
      isBlocked: false,
      latestMessage: message,
    }

    queryClient.setQueryData(getConversationsPreviewOptions().queryKey, (old) => {
      if (!old) return old
      const [firstPage = [], ...restPages] = old.pages
      return {
        ...old,
        pages: [[conversation, ...firstPage], ...restPages],
      }
    })
  }

  return Object.assign(query, {
    isUnreadFilterActive: unread,
    addInConversationPreview,
  })
}
